//@flow
import _ from 'lodash'
import type { PolicyValue, YearEndPolicyValue } from 'core/service/benefit-illustration'
import { formatNumber } from 'core/service/lib/number-format'
import MESSAGES from 'core/data-model/constants/bi-messages'
import CONSTANTS from 'core/data-model/constants/messages'
import type { PolicyValueTableProps } from 'core/service/benefit-illustration/policy-value'
import { calculatePageAmountPolicyValueTable } from 'core/service/pdf-generation/products/common/logic'
import Mustache from 'mustache'
import { dashedOutOrWithPrecision } from 'quick-quote/product-common/benefit-illustration/policy-value-gio/policy-value-row'

const rowHeightConfig = { lineHeight: 0.75, margin: [0, -3, 0, 0] }
const _breakLine = (text: string) => {
  const deathBenefitPattern = /^(ความคุ้มครอง)(กรณีเสียชีวิต)$/
  const inCaseDeathPattern = /^(กรณี.+)(\(ณ สิ้นปีกรมธรรม์\))$/

  const cashBackPattern = /^(กรณีรับเงิน)(จ่ายคืน)$/

  const patterns = [deathBenefitPattern, inCaseDeathPattern, cashBackPattern]

  const breakByFirstMatched = ([p, ...rest]) => (text.match(p) ? text.replace(p, '$1\n$2') : breakByFirstMatched(rest))
  return breakByFirstMatched(patterns)
}

export const getSubtotalNonGuaranteeDividend = (rate, policyValue: PolicyValue) => {
  return {
    '-1': _.get(policyValue, 'subtotalNonGuaranteeDividend.minusonePercent', 0),
    '3': _.get(policyValue, 'subtotalNonGuaranteeDividend.threePercent', 0),
    '5': _.get(policyValue, 'subtotalNonGuaranteeDividend.fivePercent', 0),
  }[rate]
}

const buildPolicyValueTable = (rate, policyValue: PolicyValue) => {
  const policyValueTables = []
  let rowsPerPage = [51, 52, 51]
  if (rowsPerPage[0] === policyValue.yearEndPolicyValues.length) {
    rowsPerPage[0] = rowsPerPage[0] - 1
  }
  const pageAmount = calculatePageAmountPolicyValueTable(policyValue, rowsPerPage)
  _.times(pageAmount, (currentPage) => {
    policyValueTables.push({
      table: {
        headerRows: 1,
        widths: [12, 32, 40, 40, 43, 50, '*', '*', 60, '*'],
        body: [
          ...buildHeaderTable(),
          ...policyValue.yearEndPolicyValues
            .slice(
              currentPage * rowsPerPage[currentPage > 0 ? currentPage - 1 : 0],
              rowsPerPage[currentPage] * (currentPage + 1)
            )
            .map((p) => buildBodyPolicyValue(rate, p)),
        ],
      },
      layout: {
        hLineWidth: function(i, node) {
          return i === node.table.body.length - 1 ? 1.25 : 0.75
        },
        vLineWidth: function() {
          return 0.75
        },
      },
      style: 'table25PL',
    })
  })

  _.last(policyValueTables).table.body.push(...buildFooterTable(rate, policyValue))
  return policyValueTables
}

const buildHeaderTable = () => {
  return [
    [
      { text: CONSTANTS.AGE, rowSpan: 3, style: 'tableHeader', margin: [0, 30] },
      { text: MESSAGES.GIO_END_OF_POLICY, rowSpan: 3, style: 'tableHeader', margin: [0, 30] },

      { text: MESSAGES.BASIC_PREMIUM, colSpan: 2, style: 'tableHeader', margin: [0, 10] },
      { text: '' },

      {
        text: `${MESSAGES.LIFE_SAVE_PRO_CASH_DROP_VALUE_1}
        ${MESSAGES.LIFE_SAVE_PRO_CASH_DROP_VALUE_2}
        ${MESSAGES.END_OF_YEAR_POLICY_WITH_BRACKET}`,
        colSpan: 2,
        style: 'tableHeader',
      },
      { text: '' },

      {
        text: _breakLine(MESSAGES.IN_CASE_DEATH_COVERAGE_END_OF_POLICY),
        colSpan: 2,
        style: 'tableHeader',
        margin: [0, 5],
      },
      { text: '' },

      {
        text: _breakLine(MESSAGES.IN_CASE_SURRENDER_VALUE_END_OF_POLICY),
        colSpan: 2,
        style: 'tableHeader',
        margin: [0, 5],
      },
      { text: '' },
    ],
    [
      '',
      '',
      {
        text: 'ต่อปี',
        style: 'tableHeader',
        rowSpan: 2,
        margin: [0, 10],
      },
      {
        text: 'สะสม',
        style: 'tableHeader',
        rowSpan: 2,
        margin: [0, 10],
      },

      {
        text: _breakLine(MESSAGES.INCASE_OF_CASH_BACK),
        style: 'tableHeader',
        rowSpan: 2,
        margin: [0, 5],
      },
      {
        text: `${MESSAGES.LIFE_SAVE_PRO_INCASE_WITH_COMPANY}
        ${Mustache.render(MESSAGES.PERCENTS_PER_YEAR, { percent: '0.5' })}`,
        style: 'tableHeader',
        rowSpan: 2,
      },

      {
        text: `${_breakLine(MESSAGES.DEATH_COVERAGE)}\nรับรองการจ่าย`,
        style: 'tableHeader',
        rowSpan: 2,
      },
      {
        stack: [
          { text: `${_breakLine(MESSAGES.DEATH_COVERAGE)}` },
          { text: `${MESSAGES.INCLUDE_DIVIDEND}`, margin: [-12, 0, 0, 0] },
          { text: '(4),(4.2)', fontSize: 6, relativePosition: { x: -104, y: -10 } },
          '(ถ้ามี)',
        ],
        style: 'tableHeader',
        rowSpan: 2,
      },

      {
        stack: [
          { text: 'เงินค่าเวนคืนกรมธรรม์' },
          { text: 'ประกันภัย', margin: [-10, 0, 0, 0] },
          { text: '(2),(3)', fontSize: 6, relativePosition: { x: -46, y: -10 } },
          { text: 'รับรองการจ่าย' },
        ],
        style: 'tableHeader',
        rowSpan: 2,
      },
      {
        stack: [
          { text: 'เงินค่าเวนคืน\nกรมธรรม์ประกันภัย' },
          { text: `${MESSAGES.INCLUDE_DIVIDEND}`, margin: [-15, 0, 0, 0] },
          { text: '(4),(4.3)', fontSize: 6, relativePosition: { x: 28, y: -10 } },
          '(ถ้ามี)',
        ],
        style: 'tableHeader',
        rowSpan: 2,
      },
    ],
    ['', '', '', '', '', '', '', '', '', ''],
  ]
}

const buildBodyPolicyValue = (rate, yearEndPolicy: YearEndPolicyValue) => {
  const dividendValue = yearEndPolicy.dividend.dividendValue.find((value) => value.title === rate.toString())
  return [
    {
      text: yearEndPolicy.age.value,
      alignment: 'center',
      ...rowHeightConfig,
    },
    {
      text: yearEndPolicy.yearEndPolicy,
      alignment: 'center',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.annualBasicPremium, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.cumulativeBasicPremium, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.cashDrop, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.lowSavingCashDrop, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.deathBenefit, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(dividendValue.deathBenefitPlusNonGuaranteeDividend, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(yearEndPolicy.surrenderCash, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
    {
      text: dashedOutOrWithPrecision(dividendValue.surrenderCashPlusNonGuaranteeDividendSurrender, 0),
      alignment: 'right',
      ...rowHeightConfig,
    },
  ]
}

const buildFooterTable = (rate, policyValue: PolicyValue) => {
  return [
    [
      {
        text: MESSAGES.FUND_RISK_TOTAL,
        alignment: 'center',
        bold: true,
        colSpan: 2,
      },
      '',
      {
        text: dashedOutOrWithPrecision(policyValue.subtotalAnnualBasicPremium, 0),
        alignment: 'right',
        bold: true,
      },
      {
        text: dashedOutOrWithPrecision(policyValue.subtotalAnnualBasicPremium, 0),
        alignment: 'right',
        bold: true,
      },
      {
        text: dashedOutOrWithPrecision(policyValue.subtotalCashDrop, 0),
        alignment: 'right',
        bold: true,
      },
      {
        text: dashedOutOrWithPrecision(policyValue.subtotalLowSavingCashDrop, 0),
        alignment: 'right',
        bold: true,
      },
      { text: '', border: [false, true, false, false] },
      { text: '', border: [false, true, false, false] },
      { text: '', border: [false, true, false, false] },
      { text: '', border: [false, true, false, false] },
    ],
    [
      {
        stack: [
          { text: 'เงินปันผล       ณ วันครบกำหนดสัญญา' },
          { text: '(4),(4.1)', fontSize: 6, relativePosition: { x: -424, y: -10 } },
          {
            text: `${Mustache.render(MESSAGES.IN_CASE_INVESTMENT_RATE, {
              rate: rate,
            })}`,
          },
        ],
        alignment: 'center',
        bold: true,
        colSpan: 4,
      },
      '',
      '',
      '',
      {
        text: formatNumber(getSubtotalNonGuaranteeDividend(rate, policyValue), 0, true),
        alignment: 'center',
        colSpan: 2,
        margin: [0, 10],
      },
      '',
      { text: '', border: [false, false, false, false] },
      { text: '', border: [false, false, false, false] },
      { text: '', border: [false, false, false, false] },
      { text: '', border: [false, false, false, false] },
    ],
  ]
}

const formula = (rate, policyValue: PolicyValue) => {
  const dividendValue = policyValue.yearEndPolicyValues[
    policyValue.yearEndPolicyValues.length - 1
  ].dividend.dividendValue.find((value) => value.title === rate.toString())

  return [
    {
      stack: ['ตัวอย่างการคำนวณ'],
      fontSize: 10,
      margin: [0, 5, 0, 0],
    },
    {
      columns: [
        {
          text: 'เงินปันผล ณ วันครบกำหนดสัญญา',
          fontSize: 10,
          width: 100,
        },
        {
          text: '=',
          fontSize: 10,
          width: 10,
        },
        {
          text: 'จำนวนเบี้ยฯ ที่ชำระมาทั้งหมด',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          width: 65,
        },
        {
          text: 'x',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          bold: true,
          width: 5,
        },
        {
          text: 'Max',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          bold: true,
          width: 12,
        },
        {
          text: '(',
          fontSize: 30,
          margin: [0, -10, 0, 0],
          width: 5,
        },
        {
          text: '0,',
          width: 5,
          margin: [0, 2, 0, 0],
          bold: true,
          fontSize: 8,
        },
        {
          stack: [
            {
              text: 'ระดับดัชนีอ้างอิง ณ วันคำนวณมูลค่าวันสุดท้าย',
              alignment: 'center',
              fontSize: 8,
              margin: [0, 1, 0, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 100,
                  y2: 0,
                  lineWidth: 1,
                },
              ],
              alignment: 'center',
            },
            {
              text: 'ระดับดัชนีอ้างอิง ณ วันคำนวณมูลค่าวันแรก',
              alignment: 'center',
              fontSize: 8,
            },
          ],
          width: 106,
        },
        {
          text: '- 100%',
          width: 20,
          margin: [0, 2, 0, 0],
          bold: true,
          fontSize: 8,
        },
        {
          text: ')',
          fontSize: 30,
          width: 7,
          margin: [-2, -10, 0, 0],
        },
        {
          text: 'x',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          bold: true,
          width: 5,
        },
        {
          text: 'อัตราการมีส่วนร่วมในเงินปันผล',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          width: 68,
        },
        {
          text: '(1)',
          fontSize: 8,
          margin: [0, -2, 0, 0],
          width: 8,
        },
        {
          text: 'x',
          fontSize: 8,
          margin: [0, 2, 0, 0],
          bold: true,
          width: 5,
        },
        {
          text: '(',
          fontSize: 30,
          margin: [0, -10, 0, 0],
          width: 5,
        },
        {
          stack: [
            {
              text: 'อัตราแลกเปลี่ยน ณ วันคำนวณมูลค่าวันสุดท้าย',
              alignment: 'center',
              fontSize: 8,
              margin: [0, 1, 0, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 100,
                  y2: 0,
                  lineWidth: 1,
                },
              ],
              alignment: 'center',
            },
            {
              text: 'อัตราแลกเปลี่ยน ณ วันคำนวณมูลค่าวันแรก',
              alignment: 'center',
              fontSize: 8,
            },
          ],
          width: 103,
        },
        {
          text: ')',
          fontSize: 30,
          width: 5,
          margin: [-2, -10, 0, 0],
        },
      ],
    },
    {
      margin: [100, 10, 0, 0],
      columns: [
        {
          text: '=',
          fontSize: 10,
          width: 10,
        },
        {
          text: dashedOutOrWithPrecision(policyValue.subtotalAnnualBasicPremium, 0, true),
          fontSize: 12,
          width: 50,
        },
        {
          text: 'x',
          fontSize: 12,
          width: 7,
        },
        {
          text: 'Max',
          fontSize: 12,
          width: 15,
        },
        {
          text: '(',
          fontSize: 30,
          margin: [0, -10, 0, 0],
          width: 5,
        },
        {
          text: '0,',
          width: 7,
          fontSize: 12,
        },
        {
          stack: [
            {
              text: formatNumber(dividendValue.currentIndex, 4, true),
              alignment: 'center',
              fontSize: 12,
              margin: [0, -1, 0, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 48,
                  y2: 0,
                  lineWidth: 1,
                },
              ],
              alignment: 'center',
            },
            {
              text: '100',
              alignment: 'center',
              fontSize: 12,
            },
          ],
          width: 55,
        },
        {
          text: '- 100%',
          width: 26,
          fontSize: 12,
        },
        {
          text: ')',
          fontSize: 30,
          width: 7,
          margin: [-2, -10, 0, 0],
        },
        {
          text: 'x',
          fontSize: 12,
          width: 5,
        },
        {
          text: '80%',
          fontSize: 12,
          alignment: 'center',
          width: 30,
        },
        {
          text: 'x',
          fontSize: 12,
          width: 5,
        },
        {
          stack: [
            {
              text: '35',
              alignment: 'center',
              fontSize: 12,
              margin: [0, -1, 0, 0],
            },
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 10,
                  y2: 0,
                  lineWidth: 1,
                },
              ],
              alignment: 'center',
            },
            {
              text: '35',
              alignment: 'center',
              fontSize: 12,
            },
          ],
          width: 15,
        },
      ],
    },
    {
      margin: [100, 0, 0, 0],
      columns: [
        {
          text: '=',
          fontSize: 10,
          width: 10,
        },
        {
          text: formatNumber(getSubtotalNonGuaranteeDividend(rate, policyValue), 0, true),
          fontSize: 12,
          width: 60,
        },
        {
          text: 'บาท',
          fontSize: 12,
          width: 15,
        },
      ],
    },
    ...remark(rate, dashedOutOrWithPrecision(dividendValue.currentIndex, 4)),
  ]
}

const remark = (rate, dividendIndex) => {
  return [
    {
      stack: [
        {
          text: MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[0],
        },
        {
          text: MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[1],
        },
      ],
      fontSize: 12,
      margin: [50, 15, 0, 5],
    },
    {
      columns: [
        {
          text: MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[2],
          bold: true,
          width: 50,
        },
        {
          text: MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[3],
          width: 200,
        },
      ],
      fontSize: 12,
    },
    {
      stack: [
        {
          text: `${Mustache.render(MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[4], {
            rate: rate,
            dividendIndex: dividendIndex,
          })}`,
        },
        MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[5],
        MESSAGES.GLOBAL_INDEX_16_6_FORMULA_REMARK[6],
      ],
      fontSize: 12,
      margin: [50, 0, 0, 0],
    },
  ]
}

const disclaimerPolicyValue = (rate) => {
  return [
    {
      text: MESSAGES.NOTE,
      fontSize: 12,
      bold: true,
      pageBreak: 'before',
    },
    {
      fontSize: 10,
      separator: ['(', ')'],
      ol: [
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_1[0],
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_1[1],
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_1[2],
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_1[3],
      ],
    },
    {
      margin: [10, 0, 0, 0],
      fontSize: 10,
      separator: ['(4.', ')'],
      ol: [
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_2[0],
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_2[1],
        MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_2[2],
      ],
    },
    ...disclaimerTable,
    {
      fontSize: 10,
      stack: [
        {
          text: `- ${Mustache.render(MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_3[0], {
            rate: rate,
            index: [-1, 3, 5].findIndex((e) => e === rate) + 1,
          })}`,
        },
        `- ${MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_3[1]}`,
        `- ${MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.note_3[2]}`,
      ],
    },
  ]
}

let disclaimerTable = [
  {
    fontSize: 10,
    alignment: 'center',
    margin: [125, 15, 0, 15],
    table: {
      widths: [130, 130],
      body: [
        [
          { text: MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.table_header[0], margin: [0, 5, 0, 0] },
          { text: MESSAGES.GLOBAL_INDEX_16_6_DISCLAIMER.table_header[1] },
        ],
        ['1-6', '0'],
        ['7', '10'],
        ['8', '20'],
        ['9', '30'],
        ['10', '40'],
        ['11', '50'],
        ['12', '60'],
        ['13', '70'],
        ['14', '80'],
        ['15', '90'],
        ['16', '100'],
      ],
    },
  },
]

export const policyValueTableExampleSection = (index, rate, { policyValue }: PolicyValueTableProps) => [
  {
    text: `ตัวอย่างที่ ${index} ${Mustache.render(MESSAGES.IN_CASE_INVESTMENT_RATE, {
      rate: rate,
    })}`,
    style: 'title',
    pageBreak: 'before',
    alignment: 'center',
  },
  ...buildPolicyValueTable(rate, policyValue),
  ...formula(rate, policyValue),
  ...disclaimerPolicyValue(rate),
]
